import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import {
  getAllLanguages,
  getLanguageById,
  createLanguage,
  updateLanguage,
  deleteLanguage,
  getLanguageDirections,
  getLanguageCategories,
  getTranslationSettings
} from "../../../redux/Actions/LanguageAction";
import { clearSelected, clearErrorState, setPage, setLimit } from "../../../redux/Slices/LanguageSlice";
import { LanguageFilters, CreateLanguageData, UpdateLanguageData } from "../../shared/model/LanguageInterfaces";

export const useLanguage = () => {
  const dispatch = useAppDispatch();
  const {
    languages,
    selectedLanguage,
    loading,
    error,
    totalCount,
    hasNextPage,
    currentPage,
    currentLimit,
    directions,
    categories,
    translationSettings
  } = useAppSelector((state) => state.language);

  const fetchLanguages = useCallback(async (filters: LanguageFilters) => {
    await dispatch(getAllLanguages(filters));
  }, [dispatch]);

  const fetchLanguageById = useCallback(async (id: string) => {
    await dispatch(getLanguageById(id));
  }, [dispatch]);

  const createNewLanguage = useCallback(async (data: CreateLanguageData) => {
    return await dispatch(createLanguage(data));
  }, [dispatch]);

  const updateExistingLanguage = useCallback(async (id: string, data: UpdateLanguageData) => {
    return await dispatch(updateLanguage({ id, data }));
  }, [dispatch]);

  const deleteExistingLanguage = useCallback(async (id: string) => {
    return await dispatch(deleteLanguage(id));
  }, [dispatch]);

  const clearSelectedLanguage = useCallback(() => {
    dispatch(clearSelected());
  }, [dispatch]);

  const clearError = useCallback(() => {
    dispatch(clearErrorState());
  }, [dispatch]);

  const setCurrentPage = useCallback((page: number) => {
    dispatch(setPage(page));
  }, [dispatch]);

  const setCurrentLimit = useCallback((limit: number) => {
    dispatch(setLimit(limit));
  }, [dispatch]);

  const fetchLanguageDirections = useCallback(async () => {
    await dispatch(getLanguageDirections());
  }, [dispatch]);

  const fetchLanguageCategories = useCallback(async () => {
    await dispatch(getLanguageCategories());
  }, [dispatch]);

  const fetchTranslationSettings = useCallback(async () => {
    await dispatch(getTranslationSettings());
  }, [dispatch]);

  return {
    languages,
    selectedLanguage,
    loading,
    error,
    totalCount,
    hasNextPage,
    currentPage,
    currentLimit,
    directions,
    categories,
    translationSettings,
    fetchLanguages,
    fetchLanguageById,
    createNewLanguage,
    updateExistingLanguage,
    deleteExistingLanguage,
    clearSelected: clearSelectedLanguage,
    clearErrorState: clearError,
    setPage: setCurrentPage,
    setLimit: setCurrentLimit,
    fetchLanguageDirections,
    fetchLanguageCategories,
    fetchTranslationSettings
  };
};
